import { STATION_RUN_RULES } from '../../../../shared/stationRunRules';

/** Ray/şerit ölçüleri (m). Şerit 0 sol, 1 orta, 2 sağ. */
export const TRACK = {
  lanes: 3, laneWidth: 2.6,
  /** Bölümde engel sıralarının başlangıç konumları (bölüm başından m). Kalkıştan sonraki 18 m ve istasyona son 20 m boştur. */
  rowOffsets: [24, 44, 64] as const,
  trainLength: 9, trainHeight: 3.3,
  barrierHeight: 1.0, barrierDepth: 0.5,
  signBottom: 1.2, signTop: 2.7, signDepth: 0.4,
  obstacleHalfWidth: 1.1,
  coinSpacing: 2.4, coinY: 1.0,
  /** Hareketli tren: oyuncu bu mesafeye gelince harekete geçer ve bu hızla yaklaşır. */
  movingTrainTrigger: 55, movingTrainSpeed: 7, movingTrainLead: 30,
} as const;

export type ObstacleKind = 'barrier' | 'sign' | 'train' | 'moving-train';
export type Obstacle = {
  id: string; segment: number; kind: ObstacleKind; lane: number;
  /** Mutlak mesafe (m): engelin oyuncuya bakan ön yüzü ve arka yüzü. Hareketli trende başlangıç konumu. */
  start: number; end: number;
  /** Çarpılan engel temizlenir (yeniden doğunca yol açılır). */
  cleared: boolean;
};
export type Coin = { id: string; segment: number; lane: number; at: number; y: number; taken: boolean };
export type SegmentLayout = { index: number; obstacles: Obstacle[]; coins: Coin[] };

export function laneX(lane: number) { return (lane - 1) * TRACK.laneWidth; }

/** Yayın tohumu + bölüm numarasından tekrarlanabilir rastgele sayı üretici (mulberry32). */
function rng(seed: string, segment: number) {
  let h = 2166136261 ^ segment;
  for (let i = 0; i < seed.length; i += 1) h = Math.imul(h ^ seed.charCodeAt(i), 16777619);
  let state = h >>> 0;
  return () => {
    state = (state + 0x6d2b79f5) >>> 0;
    let t = state;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

function pick<T>(random: () => number, items: readonly T[]): T { return items[Math.floor(random() * items.length) % items.length]; }
/** Fisher–Yates: karşılaştırmalı rastgele sıralamanın aksine her motorda aynı sonucu verir. */
function shuffle<T>(random: () => number, items: T[]): T[] {
  for (let i = items.length - 1; i > 0; i -= 1) { const j = Math.floor(random() * (i + 1)); [items[i], items[j]] = [items[j], items[i]]; }
  return items;
}

/**
 * Bir bölümün engel ve altın dizilimi. Zorluk bölümle artar ama her sırada en az bir şerit ya zıplama/kayma ile ya da
 * şerit değiştirerek geçilebilir; iki sıra arası (≥ 11 m) iki şerit değiştirmeye yeter.
 * - Bölüm 0–1: tek engel. 2–4: bir-iki engel. 5+: iki engel veya üç şeritte bariyer/tabela (zıpla/kay).
 * - Bölüm 3+: bazen ortadaki sıra hareketli tren olur; o şerit diğer sıralarda boş tutulur.
 */
export function createSegment(seed: string, index: number): SegmentLayout {
  const random = rng(seed, index);
  const base = index * STATION_RUN_RULES.segmentMeters;
  const obstacles: Obstacle[] = [];
  const coins: Coin[] = [];
  const movingLane = index >= 3 && random() < 0.5 ? Math.floor(random() * 3) : null;
  let freeLane = 1;
  TRACK.rowOffsets.forEach((offset, row) => {
    const at = base + offset;
    const add = (kind: ObstacleKind, lane: number) => {
      const depth = kind === 'barrier' ? TRACK.barrierDepth : kind === 'sign' ? TRACK.signDepth : TRACK.trainLength;
      const start = kind === 'moving-train' ? at + TRACK.movingTrainLead : at;
      obstacles.push({ id: `${index}-${row}-${lane}`, segment: index, kind, lane, start, end: start + depth, cleared: false });
    };
    if (movingLane !== null && row === 1) { add('moving-train', movingLane); freeLane = (movingLane + 1) % 3; return; }
    const lanes = [0, 1, 2].filter((lane) => lane !== movingLane);
    const pattern = index <= 1 ? 'single' : index <= 4 ? pick(random, ['single', 'double'] as const) : pick(random, ['double', 'double', 'triple'] as const);
    if (pattern === 'triple' && movingLane === null) {
      // Üç şerit de kapalı ama hepsi zıplanır ya da kayılır: tren yok.
      const kind = pick(random, ['barrier', 'sign'] as const);
      [0, 1, 2].forEach((lane) => add(random() < 0.75 ? kind : kind === 'barrier' ? 'sign' : 'barrier', lane));
      freeLane = Math.floor(random() * 3);
      return;
    }
    const count = pattern === 'single' ? 1 : Math.min(2, lanes.length - 1);
    const open = pick(random, lanes);
    const blocked = shuffle(random, lanes.filter((lane) => lane !== open)).slice(0, count);
    blocked.forEach((lane) => add(pick(random, index <= 1 ? ['barrier', 'sign', 'train'] as const : ['barrier', 'sign', 'train', 'train'] as const), lane));
    freeLane = open;
  });
  // Altınlar: her sıranın hemen önündeki açık şeritte kısa bir dizi; bariyerlerin üstünde zıplayınca alınan kavis.
  TRACK.rowOffsets.forEach((offset, row) => {
    const rowObstacles = obstacles.filter((o) => o.id.startsWith(`${index}-${row}-`) && o.kind !== 'moving-train');
    const blocked = new Set(rowObstacles.filter((o) => o.kind === 'train').map((o) => o.lane));
    const lane = rowObstacles.length === 0 ? freeLane : [0, 1, 2].find((l) => !rowObstacles.some((o) => o.lane === l) && l !== movingLane)
      ?? rowObstacles.find((o) => o.kind === 'barrier')?.lane ?? [0, 1, 2].find((l) => !blocked.has(l) && l !== movingLane);
    if (lane === undefined) return;
    const barrier = rowObstacles.find((o) => o.lane === lane && o.kind === 'barrier');
    for (let c = 0; c < 5; c += 1) {
      const at = base + offset - 6 + c * TRACK.coinSpacing;
      const arc = barrier ? Math.sin((c / 4) * Math.PI) * 1.4 : 0;
      coins.push({ id: `${index}-${row}-c${c}`, segment: index, lane, at, y: TRACK.coinY + arc, taken: false });
    }
  });
  return { index, obstacles, coins: coins.filter((coin) => !obstacles.some((o) => o.lane === coin.lane && o.kind !== 'barrier' && coin.at >= o.start - 0.6 && coin.at <= o.end + 0.6)) };
}
